<template>
  <section class="rules-section" :class="`theme-${track}`">
    <div class="layout-content">
      <header class="section-heading">
        <h2>{{ rulesHeading }}</h2>
        <span>{{ sectionIntro }}</span>
      </header>

      <div class="rule-groups">
        <article v-for="group in ruleGroups" :key="group.key" class="rule-group">
          <ol class="rule-list">
            <li v-for="item in group.items" :key="item.title" :class="{ pending: item.pending }">
              <span>{{ item.number }}</span>
              <div>
                <strong>{{ item.title }}</strong><p>{{ item.text }}</p>
                <p v-if="item.note" class="rule-note">{{ item.note.text }}</p>
              </div>
            </li>
          </ol>
        </article>
      </div>

      <div class="contact-line">
        <div>
          <small>{{ isEnglish ? 'OFFICIAL CONTACT' : 'OFFICIAL CONTACT' }}</small>
          <strong>{{ isEnglish ? 'Competition enquiries' : '赛事咨询邮箱' }}</strong>
        </div>
        <div class="contact-addresses">
          <div v-for="contact in contactEmails" :key="contact.email" class="contact-address">
            <span>{{ contact.label }}</span>
            <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'

export default {
  props: {
    data: { type: Object, required: true },
    track: { type: String, default: 'domestic' },
    language: { type: String, default: 'zh' }
  },
  computed: {
    ...mapState(['isEnLang']),
    isEnglish () { return this.isEnLang || (this.track === 'international' && this.language === 'en') },
    rulesHeading () {
      if (this.isEnglish) return this.track === 'domestic' ? 'Domestic Track Registration Rules' : 'International Track Registration Rules'
      return this.track === 'domestic' ? '国内赛道报名规则如下：' : '国际赛道报名规则如下：'
    },
    sectionIntro () {
      if (this.isEnglish) return this.track === 'domestic' ? 'These rules apply to both the Domestic AI Track and the Data Analytics Track.' : 'These rules apply to the International Artificial Intelligence Track.'
      return this.track === 'domestic' ? '本规则适用于人工智能赛道（国内赛道）与数据分析赛道。' : '本规则适用于人工智能赛道（国际赛道）。'
    },
    contactEmails () {
      if (this.track === 'domestic') return [
        { label: this.isEnglish ? 'Artificial Intelligence Track' : '人工智能赛道', email: 'fintechathon@g-ican.com' },
        { label: this.isEnglish ? 'Data Analytics Track' : '数据分析赛道', email: 'clubear@clubear.org' }
      ]
      return [{ label: this.isEnglish ? 'International Artificial Intelligence Track' : '人工智能赛道（国际赛道）', email: 'fintechathon@ican-x.com' }]
    },
    ruleGroups () {
      if (this.track === 'international') return [this.internationalRules]
      return this.domesticRules
    },
    domesticRules () {
      if (this.isEnglish) return [
        { key: 'domestic', index: '01', eyebrow: 'DOMESTIC TRACKS', title: 'Domestic Competition Rules', items: this.numberItems([
          ['Eligibility', 'All team members must be full-time students, including undergraduate, master’s and doctoral students, with no restrictions on major or year of study. The Domestic Track is open to students enrolled at universities in mainland China, Hong Kong, Macao and Taiwan, as well as international students, exchange students and visiting students enrolled at universities in China. Students enrolled at overseas universities, including Chinese nationals, should enter the International Track.'],
          ['Team formation', 'Only team entries are accepted. Each team must have 2–5 members, and information for every member must be completed before submission. Cross-year, cross-major and cross-university teams are allowed.'],
          ['Information authenticity', 'Participants must ensure that all registration information is truthful, accurate and valid. Each person may join only one team; duplicate registration is invalid.'],
          ['Independent completion and integrity', 'Teams must complete their entries independently and comply with applicable laws and regulations. Cheating, fraud and infringement of others’ intellectual property rights are prohibited. If a violation is discovered or reported and verified during the competition, the Organising Committee may revoke the team’s eligibility and results.'],
          ['Intellectual property', 'The intellectual property rights of competition entries belong to the participants. The organiser may use entries, related entry information and team information in promotional materials, publications, designated and authorised media, official website display and download, and exhibitions including touring exhibitions.'],
          ['Fees and finalist support', 'No registration fee is charged. Accommodation and round-trip transportation for invited finalists during the final will be arranged by the Organising Committee in accordance with policies announced in subsequent notices.']
        ]) }
      ]
      return [
        { key: 'domestic', index: '01', eyebrow: 'DOMESTIC TRACKS', title: '国内赛道通用规则', items: this.numberItems([
          ['参赛对象', '参赛团队全员须为全日制在校学生（包括本科生、硕士研究生及博士研究生），专业、年级不限；本赛道面向中国内地及港澳台地区高校在读学生，以及在华高校就读的国际学生（含留学生、交换生、借读生）。'],
          ['组队要求', '仅接受团队报名，每支参赛队伍人数 2—5 人，报名时须填写全部队员信息方可提交；允许跨年级、跨专业、跨校组队。'],
          ['报名与信息真实性', '参赛队员须保证报名信息真实、准确、有效，每人只能参加一支队伍，重复报名无效。'],
          ['独立完成与诚信', '参赛队伍须独立完成题目，符合相关法律法规规定，不得存在任何作弊、欺诈、侵犯他人知识产权等行为。参赛期间若经发现或被举报并核实存在上述行为，大赛组委会有权取消参赛队伍的参赛资格和比赛成绩。'],
          ['知识产权', '本次大赛参赛作品的知识产权归参赛选手所有，主办方有权将参赛作品及作品相关信息、参赛队伍信息用于宣传品、相关出版物、指定及授权媒体发布、官方网站浏览及下载、展览（含巡展）等。'],
          ['费用与决赛保障', '本次竞赛不收取任何报名费用。受邀参加决赛的参赛队员，决赛期间的食宿及往返交通费用由大赛组委会统一安排，相关政策请见后续具体通知。']
        ]) }
      ]
    },
    internationalRules () {
      const items = this.isEnglish ? [
        ['Eligibility', 'The competition is open to full-time undergraduate, master’s and doctoral students enrolled at overseas universities or research institutes, regardless of nationality, major or year of study. “Overseas universities” means higher education institutions and research institutes established outside China (including Hong Kong, Macao and Taiwan), with independent educational qualifications and the ability to confer academic qualifications or degrees.'],
        ['Team formation', 'Participation is team-based. Each team must have 2–5 members. Cross-year, cross-major, cross-university and cross-country teams are allowed. Non-Chinese nationals must account for at least 60% of each team. The Organising Committee will determine track eligibility based on members’ nationalities, institutions and supporting documents. A team that does not meet the International Track requirements may be reassigned to the appropriate track or disqualified.', {
          text: 'Important note: To reflect the international character of the International Track, teams are encouraged to demonstrate diversity and international representation in their membership and to include students of different nationalities and cultural backgrounds. Where the calculation does not result in a whole number, the minimum is rounded up to the next whole person (for example, a 3-member team must include at least 2 non-Chinese members).'
        }],
        ['Registration and information authenticity', 'During registration, teams must truthfully provide information for every member, including name, country of nationality, type and number of a valid personal identification document such as a passport or other valid identity document, institution and proof of enrolment or student status, major, year of study and contact details. All information must be truthful, accurate and valid. Each person may join only one team across the Domestic and International Tracks; duplicate registration is invalid.'],
        ['Official language', 'English is the official language of the International Track. Entry submission, presentations and judging are conducted in English, and submitted materials must include an English version.'],
        ['Independent completion and integrity', 'Teams must complete their entries independently and comply with applicable laws and regulations. Cheating, fraud, plagiarism and infringement of others’ intellectual property rights are prohibited. If a violation is discovered or reported and verified, the Organising Committee may revoke the team’s eligibility and results.'],
        ['Compliance of entries and third-party materials', 'Data, code, open-source components, APIs and other third-party materials used in an entry must be legally authorised and comply with applicable open-source licences and data-use terms. The team bears responsibility for infringement or improper use.'],
        ['Intellectual property', 'The intellectual property rights of competition entries belong to the participants. The organiser may use entries and team information in promotional materials, publications, designated and authorised media, official website display and download, and exhibitions including touring exhibitions for non-commercial publicity. This licence is non-exclusive and royalty-free.']
      ] : [
        ['参赛对象', '本届竞赛面向国外高校及科研院所全日制在读学生（含本科生、硕士研究生、博士研究生），不限国籍、专业与年级。本规则所称“国外高校”，指设立在除中国（含香港、澳门、台湾地区）以外的国家，具有独立办学资质、可独立授予学历或学位的高等学校及科研机构。'],
        ['组队要求', '须以团队形式参赛，每队 2—5 人，允许跨年级、跨专业、跨校及跨国组队。每支队伍中非中国籍成员占比须不低于 60%，组委会将结合队员国籍、就读院校及相关证明材料综合认定赛道归属。不符合国际赛道条件的，组委会有权调剂至相应赛道或取消参赛资格。', {
          text: '温馨提示：为体现国际赛道的国际化特色，鼓励参赛团队在成员构成上体现多元化与国际化，支持不同国籍、不同文化背景的学生联合组队。人数计算结果不足一人时，按一人向上取整（例如，3 人团队须至少有 2 名非中国籍成员）。'
        }],
        ['报名与信息真实性', '报名时须如实填写全部队员信息（含姓名、所属国家（国籍）、有效个人证件类型及号码（护照或其他有效身份证件）、就读院校及在读证明（学籍信息）、专业、年级、联系方式），信息须真实、准确、有效。每人仅可加入一支队伍（国内赛道与国际赛道合计），重复报名无效。'],
        ['参赛语言（国际赛道）', '本赛道官方语言为英文。作品提交、答辩与评审均以英文进行；提交材料应包含英文版本。'],
        ['独立完成与诚信', '参赛队伍须独立完成作品，遵守相关法律法规，不得存在作弊、欺诈、抄袭或侵犯他人知识产权等行为。经发现或举报核实，组委会有权取消其参赛资格及成绩。'],
        ['作品与第三方素材合规', '作品中使用的数据、代码、开源组件、API 及其他第三方素材，须合法取得授权并符合相应开源许可及数据使用条款；因作品侵权或违规使用产生的责任，由参赛队伍自行承担。'],
        ['知识产权', '参赛作品知识产权归参赛选手所有。主办方有权将作品及队伍信息用于宣传品、出版物、指定及授权媒体发布、官网浏览与下载、展览（含巡展）等非商业性宣传用途。该授权为非排他、无偿授权。']
      ]
      return { key: 'international', index: '01', eyebrow: 'INTERNATIONAL TRACK', title: this.isEnglish ? 'International Artificial Intelligence Track' : '人工智能赛道（国际赛道）', items: this.numberItems(items) }
    }
  },
  methods: {
    numberItems (items) {
      return items.map((item, index) => ({
        number: `${index + 1}.`,
        title: item[0],
        text: item[1],
        note: item[2] && typeof item[2] === 'object' ? item[2] : null,
        pending: item[2] === true || Boolean(item[3])
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.rules-section { --accent:#315fd8; --rule-number:#df4b47; padding:72px 0 84px; background:#fff; }
.theme-international { --accent:#078da9; }
.section-heading { margin:0 0 38px; padding-bottom:25px; border-bottom:1px solid #e8ebf0; text-align:left; }
.section-heading h2 { color:#252b34; font-size:28px; font-weight:700; line-height:1.45; }
.section-heading > span { display:block; margin-top:10px; color:#737d8d; font-size:14px; line-height:1.75; }
.rule-groups { display:block; max-width:1180px; }
.rule-group { background:#fff; }
.rule-list { padding:0; }
.rule-list li { display:grid; grid-template-columns:40px 1fr; gap:17px; padding:0 0 31px; }
.rule-list li:last-child { padding-bottom:0; }
.rule-list li > span { padding-top:1px; color:var(--rule-number); font-size:16px; font-weight:700; line-height:1.95; }
.rule-list li > div { font-size:0; }
.rule-list strong { display:inline; margin-right:20px; color:#252b34; font-size:16px; line-height:1.95; overflow-wrap:anywhere; }
.rule-list p { display:inline; color:#525d6e; font-size:15px; line-height:2; overflow-wrap:anywhere; word-break:break-word; }
.rule-list .pending strong::after { content:'待确认'; margin-left:8px; padding:2px 6px; border-radius:999px; color:#a66c12; font-size:9px; background:#fff5df; }
.rule-list .rule-note { display:block; margin-top:6px; color:#778292; font-size:12.5px; line-height:1.65; }
.contact-line { display:flex; align-items:center; justify-content:space-between; gap:28px; margin-top:42px; padding:24px 28px; border-left:4px solid var(--accent); color:#687286; background:#f7f9fd; }
.contact-line small,.contact-line strong { display:block; }
.contact-line small { color:var(--accent); font-size:10px; font-weight:700; letter-spacing:.14em; }
.contact-line strong { margin-top:5px; color:#273149; font-size:16px; }
.contact-addresses { display:flex; align-items:flex-end; flex-direction:column; gap:13px; }
.contact-address { display:flex; align-items:baseline; justify-content:flex-end; gap:14px; }
.contact-address span { color:#687286; font-size:13px; font-weight:600; white-space:nowrap; }
.contact-line a { color:var(--accent); font-size:20px; font-weight:700; line-height:1.4; text-decoration:none; word-break:break-all; }
.contact-line a:hover { text-decoration:underline; text-underline-offset:4px; }
@include mobile {
  .rules-section { padding:52px 0 60px; }
  .section-heading { margin-bottom:30px; }
  .section-heading h2 { font-size:24px; }
  .section-heading > span { font-size:13px; }
  .rule-list li { grid-template-columns:30px 1fr; gap:9px; padding-bottom:26px; }
  .rule-list li > span { font-size:15px; }
  .rule-list strong { display:block; margin:0 0 5px; font-size:15.5px; }
  .rule-list p { display:block; font-size:14.5px; line-height:1.9; }
  .rule-list .rule-note { margin-top:5px; font-size:12px; line-height:1.6; }
  .contact-line { flex-direction:column; align-items:flex-start; padding:18px; }
  .contact-addresses { align-items:flex-start; width:100%; }
  .contact-address { align-items:flex-start; flex-direction:column; gap:3px; }
  .contact-address span { font-size:12px; }
  .contact-line a { font-size:18px; }
}
</style>
